import React, { useState } from 'react';
import { recipes } from '../mockData/mock';
import { Clock, ChefHat, Filter } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';

const Recipes = () => {
  const [selectedDifficulty, setSelectedDifficulty] = useState('all');
  const [selectedRecipe, setSelectedRecipe] = useState(null);

  const filteredRecipes = selectedDifficulty === 'all'
    ? recipes
    : recipes.filter(recipe => recipe.difficulty.toLowerCase() === selectedDifficulty);

  const difficulties = ['all', 'beginner', 'intermediate'];

  return (
    <div className="min-h-screen bg-[#f7f5f2]">
      {/* Hero Section */}
      <section className="bg-gradient-to-b from-white to-[#f7f5f2] py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center justify-center space-x-2 bg-[#a8b5a0]/10 px-4 py-2 rounded-full mb-6">
            <ChefHat className="h-5 w-5 text-[#a8b5a0]" />
            <span className="text-sm font-medium text-[#736c64]">Beginner-Friendly Recipes</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-[#1e1919] mb-6">
            Sourdough Recipes
          </h1>
          <p className="text-xl text-[#736c64] max-w-2xl mx-auto">
            Simple, foolproof recipes to help you create delicious sourdough bread and make the most of your starter.
          </p>
        </div>
      </section>

      {/* Filter Section */}
      <section className="py-8 bg-white border-b border-[#e8e6e1]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Filter className="h-5 w-5 text-[#736c64]" />
              <span className="text-sm font-medium text-[#736c64]">Filter by difficulty:</span>
            </div>
            <div className="flex gap-2">
              {difficulties.map((difficulty) => (
                <Button
                  key={difficulty}
                  variant={selectedDifficulty === difficulty ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setSelectedDifficulty(difficulty)}
                  className={selectedDifficulty === difficulty 
                    ? 'bg-[#a8b5a0] hover:bg-[#8fa087] text-white' 
                    : 'border-[#e8e6e1] text-[#736c64] hover:bg-[#f7f5f2]'
                  }
                >
                  {difficulty.charAt(0).toUpperCase() + difficulty.slice(1)}
                </Button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Recipes Grid */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredRecipes.map((recipe) => (
              <Card 
                key={recipe.id} 
                className="overflow-hidden hover:shadow-lg transition-all cursor-pointer group"
                onClick={() => setSelectedRecipe(recipe)}
              >
                <div className="relative h-56 overflow-hidden">
                  <img
                    src={recipe.image}
                    alt={recipe.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute top-4 right-4">
                    <Badge className="bg-white text-[#1e1919] hover:bg-white">
                      {recipe.difficulty}
                    </Badge>
                  </div>
                </div>
                <CardContent className="p-6">
                  <h3 className="text-xl font-semibold text-[#1e1919] mb-2 group-hover:text-[#a8b5a0] transition-colors">
                    {recipe.title}
                  </h3>
                  <div className="flex items-center gap-4 text-sm text-[#736c64] mb-4">
                    <div className="flex items-center gap-1">
                      <Clock className="h-4 w-4" />
                      <span>{recipe.time}</span>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {recipe.tags.slice(0, 2).map((tag, index) => (
                      <span
                        key={index}
                        className="text-xs px-2 py-1 bg-[#a8b5a0]/10 text-[#736c64] rounded-full"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {filteredRecipes.length === 0 && (
            <div className="text-center py-16">
              <p className="text-lg text-[#736c64]">
                No recipes found for this difficulty level.
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Recipe Detail Modal */}
      {selectedRecipe && (
        <div 
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedRecipe(null)}
        >
          <div 
            className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative h-64 md:h-80">
              <img
                src={selectedRecipe.image}
                alt={selectedRecipe.title}
                className="w-full h-full object-cover"
              />
              <button
                onClick={() => setSelectedRecipe(null)}
                className="absolute top-4 right-4 bg-white rounded-full p-2 shadow-lg hover:bg-[#f7f5f2] transition-colors"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="p-8">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-3xl font-bold text-[#1e1919] mb-2">
                    {selectedRecipe.title}
                  </h2>
                  <div className="flex items-center gap-4 text-sm text-[#736c64]">
                    <Badge className="bg-[#a8b5a0]/20 text-[#736c64] hover:bg-[#a8b5a0]/20">
                      {selectedRecipe.difficulty}
                    </Badge>
                    <div className="flex items-center gap-1">
                      <Clock className="h-4 w-4" />
                      <span>{selectedRecipe.time}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="space-y-8">
                {/* Beginner Notes */}
                {selectedRecipe.notes && selectedRecipe.notes.length > 0 && (
                  <div className="bg-[#f7f5f2] border border-[#e8e6e1] rounded-xl p-5">
                    <h3 className="text-lg font-semibold text-[#1e1919] mb-3">Beginner Notes</h3>
                    <ul className="space-y-2">
                      {selectedRecipe.notes.map((note, index) => (
                        <li key={index} className="flex items-start gap-2 text-[#736c64]">
                          <span className="text-[#a8b5a0] mt-1">•</span>
                          <span>{note}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Ingredients */}
                <div>
                  <h3 className="text-xl font-semibold text-[#1e1919] mb-4">Ingredients</h3>
                  <ul className="space-y-2">
                    {selectedRecipe.ingredients.map((ingredient, index) => (
                      <li key={index} className="flex items-start gap-2 text-[#736c64]">
                        <span className="text-[#a8b5a0] mt-1">•</span>
                        <span>{ingredient}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Instructions */}
                <div>
                  <h3 className="text-xl font-semibold text-[#1e1919] mb-4">Instructions</h3>
                  <ol className="space-y-4">
                    {selectedRecipe.instructions.map((instruction, index) => (
                      <li key={index} className="flex gap-4">
                        <span className="flex-shrink-0 w-8 h-8 bg-[#a8b5a0] text-white rounded-full flex items-center justify-center font-semibold text-sm">
                          {index + 1}
                        </span>
                        <span className="text-[#736c64] pt-1">{instruction}</span>
                      </li>
                    ))}
                  </ol>
                </div>

                {/* Tags */}
                {selectedRecipe.tags && selectedRecipe.tags.length > 0 && (
                  <div>
                    <h3 className="text-xl font-semibold text-[#1e1919] mb-4">Tags</h3>
                    <div className="flex flex-wrap gap-2">
                      {selectedRecipe.tags.map((tag, index) => (
                        <span
                          key={index}
                          className="px-3 py-1 bg-[#a8b5a0]/10 text-[#736c64] rounded-full text-sm"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Recipes;
